import {
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import { safeHref } from '@ecom/contracts';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count } from '@/lib/format';
import {
  continueList,
  htmlToRichText,
  insertLink,
  lineStyleAt,
  textStats,
  toggleBold,
  toggleLineStyle,
  type Language,
  type LineStyle,
  type TextSelection,
} from '@/lib/content-pages';
import {
  CloseIcon,
  ExpandIcon,
  EyeIcon,
  LinkIcon,
  ListIcon,
  ListOrderedIcon,
  PencilIcon,
  QuestionIcon,
  RedoIcon,
  ShrinkIcon,
  SplitIcon,
  UndoIcon,
} from '@/components/icons';
import { useDismiss } from '@/components/menu';
import { Button, cx } from '@/components/ui';
import { PagePreview } from './preview';

/**
 * A content page's body editor (ADR-058): a toolbar over a text box, writing
 * the pages' small formatting — headings, lists, bold, links — rather than
 * HTML, so what is saved is still text the storefront parses into elements.
 *
 * It reads as a word processor where it matters: the toolbar shows what the
 * cursor's line is, Enter carries a list on, ⌘B and ⌘K do what they do
 * everywhere, a page pasted from Word or Google Docs keeps its headings,
 * lists, bold and links, and undo undoes a toolbar edit like a keystroke. A
 * preview beside it or in its place reads the draft back with the storefront's
 * own parser; focus mode gives a long policy the whole window.
 */

export interface EditorHandle {
  /** Puts the cursor at the start of a line and brings it into view — the outline's jump. */
  focusLine: (line: number) => void;
}

type Mode = 'write' | 'split' | 'preview';

const MODE_KEY = 'admin.pages.editorMode';

function storedMode(): Mode {
  try {
    const value = window.localStorage.getItem(MODE_KEY);
    return value === 'split' || value === 'preview' ? value : 'write';
  } catch {
    return 'write';
  }
}

export function RichTextEditor({
  value,
  onChange,
  language,
  title,
  links,
  label,
  editorRef,
  aside,
}: {
  value: string;
  onChange: (next: string) => void;
  language: Language;
  /** The page's title in this language, for the preview. */
  title: string;
  /** The store's other pages, offered when linking. */
  links: { label: string; href: string }[];
  label: string;
  editorRef?: Ref<EditorHandle>;
  /** Shown in the toolbar's far end — the translate button. */
  aside?: ReactNode;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const area = useRef<HTMLTextAreaElement>(null);
  const pending = useRef<{ start: number; end: number } | null>(null);
  const [mode, setMode] = useState<Mode>(storedMode);
  const [focus, setFocus] = useState(false);
  const [linking, setLinking] = useState<{ text: string; start: number; end: number } | null>(null);
  const [guide, setGuide] = useState(false);
  const [style, setStyle] = useState<LineStyle | null>(null);
  const dir = language === 'ar' ? 'rtl' : 'ltr';
  const stats = textStats(value);

  useImperativeHandle(editorRef, () => ({
    focusLine(line: number) {
      const element = area.current;
      if (!element) return;
      if (mode === 'preview') changeMode('write');
      const offset = value.split('\n').slice(0, line).reduce((sum, row) => sum + row.length + 1, 0);
      requestAnimationFrame(() => {
        element.focus({ preventScroll: true });
        element.setSelectionRange(offset, offset);
        // A text box scrolls to its cursor only when it scrolls itself; this
        // one grows, so whatever holds it scrolls — the admin's main column,
        // or focus mode's own pane. Wrapped lines make it near, not exact.
        const scroller = (focus ? element.closest('[data-focus-scroll]') : element.closest('#main')) as HTMLElement | null;
        if (!scroller) return;
        const lineHeight = parseFloat(getComputedStyle(element).lineHeight) || 28;
        const top = element.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
        scroller.scrollTo({ top: Math.max(0, top + line * lineHeight - 140), behavior: 'smooth' });
      });
    },
  }));

  function changeMode(next: Mode) {
    setMode(next);
    try {
      window.localStorage.setItem(MODE_KEY, next);
    } catch {
      // A browser that keeps nothing still edits; it just forgets the choice.
    }
  }

  // The box grows with its text: one scroll for the page, not one inside another.
  useLayoutEffect(() => {
    const element = area.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight + 2}px`;
    if (pending.current) {
      element.setSelectionRange(pending.current.start, pending.current.end);
      pending.current = null;
    }
  }, [value, mode, focus]);

  // Escape leaves focus mode — and only that, so it does not also close a dialog beneath.
  useEffect(() => {
    if (!focus) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !linking) setFocus(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focus, linking]);

  const selection = (): TextSelection => {
    const element = area.current;
    return { value, start: element?.selectionStart ?? value.length, end: element?.selectionEnd ?? value.length };
  };

  /**
   * Applies an edit as the browser's own typing would, so ⌘Z undoes it: the
   * changed stretch is selected and replaced with `insertText`. Where a
   * browser will not, the value is set and the selection restored after.
   */
  function apply(next: TextSelection) {
    const element = area.current;
    if (!element) return;
    if (next.value === value) {
      element.focus();
      element.setSelectionRange(next.start, next.end);
      return;
    }
    let head = 0;
    while (head < value.length && head < next.value.length && value[head] === next.value[head]) head += 1;
    let tail = 0;
    while (
      tail < value.length - head &&
      tail < next.value.length - head &&
      value[value.length - 1 - tail] === next.value[next.value.length - 1 - tail]
    )
      tail += 1;
    const replacement = next.value.slice(head, next.value.length - tail);
    element.focus();
    element.setSelectionRange(head, value.length - tail);
    pending.current = { start: next.start, end: next.end };
    const done = replacement ? document.execCommand('insertText', false, replacement) : document.execCommand('delete');
    if (!done || element.value !== next.value) onChange(next.value);
    else element.setSelectionRange(next.start, next.end);
    setStyle(lineStyleAt(next.value, next.start));
  }

  const lineStyle = (target: LineStyle) => apply(toggleLineStyle(selection(), target));
  const bold = () => apply(toggleBold(selection(), t('editorBoldPlaceholder')));
  const openLink = () => {
    const current = selection();
    setLinking({ text: value.slice(current.start, current.end), start: current.start, end: current.end });
  };

  const writing = mode !== 'preview';
  const previewing = mode !== 'write';

  const tools: { id: string; label: string; shortcut?: string; active?: boolean; onClick: () => void; icon: ReactNode }[] = [
    { id: 'h2', label: t('editorHeading'), active: style === 'h2', onClick: () => lineStyle('h2'), icon: <Glyph>H2</Glyph> },
    { id: 'h3', label: t('editorSubheading'), active: style === 'h3', onClick: () => lineStyle('h3'), icon: <Glyph>H3</Glyph> },
    { id: 'bold', label: t('editorBold'), shortcut: '⌘B', onClick: bold, icon: <Glyph bold>B</Glyph> },
    { id: 'bullet', label: t('editorBullets'), active: style === 'bullet', onClick: () => lineStyle('bullet'), icon: <ListIcon className="size-[18px]" /> },
    { id: 'number', label: t('editorNumbers'), active: style === 'number', onClick: () => lineStyle('number'), icon: <ListOrderedIcon className="size-[18px]" /> },
    { id: 'link', label: t('editorLink'), shortcut: '⌘K', onClick: openLink, icon: <LinkIcon className="size-[18px]" /> },
  ];

  return (
    <div
      // A container: the toolbar and the split view fit the editor's own width,
      // which a three-column page makes narrower than the window suggests.
      className={cx(
        '@container flex flex-col',
        focus ? 'fixed inset-0 z-50 bg-surface' : 'rounded-control border border-line bg-surface focus-within:border-brand-400',
      )}
    >
      {/* The toolbar sticks under the window's top while a long page scrolls past it. */}
      <div
        role="toolbar"
        aria-label={t('editorToolbar')}
        className={cx(
          'relative z-10 flex flex-wrap items-center gap-1 border-b border-line bg-surface/95 px-2 py-1.5 backdrop-blur',
          focus ? 'px-4 py-2 lg:px-8' : 'sticky top-0 rounded-t-control',
        )}
      >
        {writing ? (
          <>
            {tools.map((tool, index) => (
              <span key={tool.id} className="contents">
                {index === 2 || index === 3 || index === 5 ? <Divider /> : null}
                <ToolButton label={tool.shortcut ? `${tool.label} (${tool.shortcut})` : tool.label} active={tool.active} onClick={tool.onClick}>
                  {tool.icon}
                </ToolButton>
              </span>
            ))}
            <Divider />
            <ToolButton label={t('editorUndo')} onClick={() => { area.current?.focus(); document.execCommand('undo'); }}>
              <UndoIcon className="size-[18px] rtl:-scale-x-100" />
            </ToolButton>
            <ToolButton label={t('editorRedo')} onClick={() => { area.current?.focus(); document.execCommand('redo'); }}>
              <RedoIcon className="size-[18px] rtl:-scale-x-100" />
            </ToolButton>
          </>
        ) : (
          <span className="px-2 text-xs text-ink-muted">{t('editorPreviewNote')}</span>
        )}

        <span className="ms-auto flex items-center gap-1">
          {aside}
          <div role="radiogroup" aria-label={t('editorView')} className="inline-flex rounded-control border border-line bg-surface-sunken p-0.5">
            {(
              [
                { id: 'write', label: t('editorWrite'), icon: <PencilIcon className="size-4" /> },
                { id: 'split', label: t('editorSplit'), icon: <SplitIcon className="size-4" /> },
                { id: 'preview', label: t('editorPreview'), icon: <EyeIcon className="size-4" /> },
              ] as const
            ).map((item) => (
              <button
                key={item.id}
                type="button"
                role="radio"
                aria-checked={mode === item.id}
                title={item.label}
                onClick={() => changeMode(item.id)}
                className={cx(
                  'items-center gap-1.5 rounded-[calc(var(--radius-control)-2px)] px-2 py-1 text-xs font-medium transition-colors',
                  // Side by side needs the room: offered only where the editor is wide enough.
                  item.id === 'split' ? 'hidden @3xl:inline-flex' : 'inline-flex',
                  mode === item.id ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink',
                )}
              >
                {item.icon}
                <span className="sr-only @2xl:not-sr-only">{item.label}</span>
              </button>
            ))}
          </div>
          <ToolButton label={focus ? t('editorExitFocus') : t('editorFocus')} onClick={() => setFocus(!focus)}>
            {focus ? <ShrinkIcon className="size-[18px]" /> : <ExpandIcon className="size-[18px]" />}
          </ToolButton>
        </span>

        {linking ? (
          <LinkPopover
            initialText={linking.text}
            links={links}
            dir={dir}
            onClose={() => {
              setLinking(null);
              area.current?.focus();
            }}
            onInsert={(text, href) => {
              const at = linking;
              setLinking(null);
              apply(insertLink({ value, start: at.start, end: at.end }, text, href));
            }}
          />
        ) : null}
      </div>

      <div
        data-focus-scroll
        className={cx(
          'grid min-h-0',
          focus && 'flex-1 overflow-y-auto',
          mode === 'split' && '@3xl:grid-cols-2 @3xl:divide-x @3xl:divide-line rtl:@3xl:divide-x-reverse',
        )}
      >
        <div className={cx(!writing && 'hidden', focus && mode !== 'split' && 'mx-auto w-full max-w-3xl')}>
          <textarea
            ref={area}
            dir={dir}
            lang={language}
            aria-label={label}
            value={value}
            spellCheck
            placeholder={t('editorPlaceholder')}
            onChange={(event) => onChange(event.target.value)}
            onSelect={(event) => setStyle(lineStyleAt(event.currentTarget.value, event.currentTarget.selectionStart))}
            onPaste={(event) => {
              const html = event.clipboardData.getData('text/html');
              if (!html) return;
              const text = htmlToRichText(html);
              if (!text) return;
              event.preventDefault();
              const current = selection();
              apply({
                value: value.slice(0, current.start) + text + value.slice(current.end),
                start: current.start + text.length,
                end: current.start + text.length,
              });
            }}
            onKeyDown={(event) => {
              const mod = event.metaKey || event.ctrlKey;
              if (mod && event.key.toLowerCase() === 'b') {
                event.preventDefault();
                bold();
              } else if (mod && event.key.toLowerCase() === 'k') {
                // In the text box ⌘K is a link, as in every editor — not the admin's search.
                event.preventDefault();
                event.stopPropagation();
                openLink();
              } else if (event.key === 'Enter' && !event.shiftKey && !mod && !event.nativeEvent.isComposing) {
                const next = continueList(selection());
                if (next) {
                  event.preventDefault();
                  apply(next);
                }
              }
            }}
            className={cx(
              'block min-h-[26rem] w-full resize-none overflow-hidden bg-transparent px-4 py-4 text-[15px] leading-7 text-ink outline-none placeholder:text-ink-faint',
              focus && 'min-h-[calc(100vh-9rem)] px-6 py-8 text-base leading-8',
            )}
          />
        </div>
        {previewing ? (
          <div className={cx('px-5 py-5', mode === 'split' && 'hidden @3xl:block', focus && 'mx-auto w-full max-w-3xl py-8')}>
            {mode === 'split' ? (
              <p className="mb-4 text-[11px] font-medium tracking-wide text-ink-faint uppercase">{t('editorPreview')}</p>
            ) : null}
            <PagePreview title={title} body={value} language={language} />
          </div>
        ) : null}
      </div>

      <div
        className={cx(
          'flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line px-4 py-2 text-xs text-ink-muted',
          focus ? 'lg:px-8' : 'rounded-b-control bg-surface-sunken/40',
        )}
      >
        <span data-numeric>{t('editorWords', { count: count(stats.words, locale) })}</span>
        <span data-numeric>{t('editorMinutes', { count: count(stats.minutes, locale) })}</span>
        <span data-numeric>{t('editorSections', { count: count(stats.sections, locale) })}</span>
        <span data-numeric>{t('editorLinks', { count: count(stats.links, locale) })}</span>
        {stats.brokenLinks.length > 0 ? (
          <span className="font-medium text-danger">{t('editorBrokenLinks', { count: count(stats.brokenLinks.length, locale) })}</span>
        ) : null}
        <button
          type="button"
          aria-expanded={guide}
          onClick={() => setGuide(!guide)}
          className="ms-auto inline-flex items-center gap-1 font-medium text-ink-muted transition-colors hover:text-ink"
        >
          <QuestionIcon className="size-4" />
          {t('editorGuide')}
        </button>
      </div>
      {guide ? <FormattingGuide /> : null}
    </div>
  );
}

function Glyph({ children, bold = false }: { children: ReactNode; bold?: boolean }) {
  return <span className={cx('text-[13px] leading-none', bold ? 'font-extrabold' : 'font-semibold')} dir="ltr">{children}</span>;
}

function Divider() {
  return <span aria-hidden className="mx-1 h-5 w-px bg-line" />;
}

function ToolButton({
  label,
  active = false,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active || undefined}
      // Keep the text box's selection: a click on the toolbar must not take focus first.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cx(
        'inline-flex size-8 items-center justify-center rounded-[calc(var(--radius-control)-2px)] transition-colors',
        active ? 'bg-brand-50 text-brand-700' : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

/** Text and an address, with the store's pages a click away — internal links are what search engines follow. */
function LinkPopover({
  initialText,
  links,
  dir,
  onInsert,
  onClose,
}: {
  initialText: string;
  links: { label: string; href: string }[];
  dir: 'rtl' | 'ltr';
  onInsert: (text: string, href: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const hrefInput = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(initialText);
  const [href, setHref] = useState('');
  const [tried, setTried] = useState(false);
  useDismiss(true, root, onClose);

  useEffect(() => {
    hrefInput.current?.focus();
  }, []);

  const valid = safeHref(href.trim()) !== null;
  const submit = () => {
    setTried(true);
    if (!valid || !text.trim()) return;
    onInsert(text.trim(), href.trim());
  };

  return (
    <div
      ref={root}
      role="dialog"
      aria-label={t('editorLink')}
      onKeyDown={(event) => {
        // Escape closes this and goes no further: focus mode stays open beneath it.
        if (event.key === 'Escape') {
          event.stopPropagation();
          onClose();
        } else if (event.key === 'Enter') {
          event.preventDefault();
          submit();
        }
      }}
      className="absolute inset-x-2 top-full z-20 mt-1 rounded-card border border-line bg-surface p-4 shadow-float sm:inset-x-auto sm:start-2 sm:w-[24rem]"
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-ink">{t('editorLinkTitle')}</p>
        <button type="button" onClick={onClose} aria-label={t('close')} className="text-ink-faint hover:text-ink">
          <CloseIcon className="size-4" />
        </button>
      </div>
      <label className="mt-3 flex flex-col gap-1 text-xs font-medium text-ink-muted" htmlFor={`${id}-text`}>
        {t('editorLinkText')}
        <input
          id={`${id}-text`}
          dir={dir}
          value={text}
          onChange={(event) => setText(event.target.value)}
          className="min-h-9 rounded-control border border-line bg-surface px-3 text-sm text-ink focus:border-brand-500"
        />
      </label>
      <label className="mt-3 flex flex-col gap-1 text-xs font-medium text-ink-muted" htmlFor={`${id}-href`}>
        {t('editorLinkHref')}
        <input
          id={`${id}-href`}
          ref={hrefInput}
          dir="ltr"
          value={href}
          placeholder="/pages/shipping · https://… · mailto:…"
          aria-invalid={tried && !valid ? true : undefined}
          onChange={(event) => setHref(event.target.value)}
          className={cx(
            'min-h-9 rounded-control border bg-surface px-3 text-sm text-ink focus:border-brand-500',
            tried && !valid ? 'border-danger' : 'border-line',
          )}
        />
      </label>
      {tried && !valid ? <p className="mt-1 text-xs text-danger">{t('editorLinkInvalid')}</p> : null}
      {links.length > 0 ? (
        <div className="mt-3">
          <p className="mb-1.5 text-[11px] text-ink-faint">{t('editorLinkPages')}</p>
          <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto">
            {links.map((link) => (
              <button
                key={link.href}
                type="button"
                onClick={() => {
                  setHref(link.href);
                  if (!text.trim()) setText(link.label);
                }}
                className={cx(
                  'rounded-full border px-2.5 py-1 text-xs transition-colors',
                  href === link.href ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-line text-ink-muted hover:bg-surface-sunken hover:text-ink',
                )}
              >
                {link.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button size="sm" onClick={submit} disabled={!href.trim() || !text.trim()}>
          {t('editorLinkInsert')}
        </Button>
      </div>
    </div>
  );
}

/** What the toolbar writes, for an operator who would rather type it. */
function FormattingGuide() {
  const t = useT();
  const rows: [string, string][] = [
    ['## …', t('guideHeading')],
    ['### …', t('guideSubheading')],
    ['**…**', t('guideBold')],
    ['- …', t('guideBullets')],
    ['1. …', t('guideNumbers')],
    ['[…](/pages/terms)', t('guideLink')],
    ['↵ ↵', t('guideParagraph')],
  ];
  return (
    <div className="border-t border-line px-4 py-3">
      <p className="mb-2 text-xs text-ink-muted">{t('guideIntro')}</p>
      <dl className="grid gap-x-6 gap-y-1.5 text-xs sm:grid-cols-2">
        {rows.map(([code, meaning]) => (
          <div key={code} className="flex items-baseline gap-3">
            <dt dir="ltr" className="w-32 shrink-0 font-mono text-ink">
              {code}
            </dt>
            <dd className="text-ink-muted">{meaning}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
